import React, { useMemo } from 'react'
import NumberFormat from 'react-number-format'

import { useTheme, Paper, Badge } from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import FlagIcon from '@material-ui/icons/Flag'
import AssignmentTurnedIn from '@material-ui/icons/AssignmentTurnedIn'
import AssignmentIcon from '@material-ui/icons/Assignment'

import { UnitConfig, UnitInspection } from '../../models'
import { toMMDDYYYY } from '../../helpers'
import { UnitInspectionStatusBadge } from '../Inspection/UnitInspectionStatusBadge'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    unitInspection: UnitInspection
    unitConfig?: UnitConfig
    selected?: boolean
    onClick?: () => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        cardContainer: {
            position: 'relative',
            marginBottom: theme.spacing(2),
            '&:last-child': {
                marginBottom: 0,
            },
        },
        timelineDot: {
            position: 'absolute',
            left: -28,
            top: 16,
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            border: `2px solid ${theme.palette.background.paper}`,
            zIndex: 1,
        },
        card: {
            padding: theme.spacing(2),
            borderRadius: theme.spacing(1.5),
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                borderColor: theme.palette.primary.light,
            },
        },
        cardSelected: {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.main,
        },
        topRow: {
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: theme.spacing(1.5),
        },
        titleSection: {
            flex: 1,
            overflow: 'hidden',
        },
        inspectionName: {
            ...(typographyStyle(theme.typography.subtitle1) as Record<
                string,
                unknown
            >),
            fontWeight: 600,
            color: theme.palette.text.primary,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginBottom: theme.spacing(0.25),
        },
        folderPath: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            fontSize: 11,
            color: theme.palette.text.secondary,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        unitName: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            fontWeight: 500,
            color: theme.palette.text.primary,
            display: 'block',
            marginTop: theme.spacing(0.25),
        },
        rightSection: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: theme.spacing(0.5),
            marginLeft: theme.spacing(2),
        },
        cost: {
            ...(typographyStyle(theme.typography.subtitle1) as Record<
                string,
                unknown
            >),
            fontWeight: 700,
            color: theme.palette.text.primary,
        },
        bottomRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            paddingTop: theme.spacing(1.5),
            borderTop: `1px solid ${theme.palette.divider}`,
        },
        assignmentInfo: {
            flex: 1,
        },
        assignedTo: {
            ...(typographyStyle(theme.typography.body2) as Record<
                string,
                unknown
            >),
            fontWeight: 500,
            color: theme.palette.text.primary,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        dateRange: {
            ...(typographyStyle(theme.typography.caption) as Record<
                string,
                unknown
            >),
            color: theme.palette.text.secondary,
        },
        badges: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1.5),
        },
    }),
)

export const InspectionCard: React.FC<Props> = React.memo((props) => {
    const { unitInspection, unitConfig, selected, onClick } = props

    const theme = useTheme()
    const classes = useStyles()

    // Calculate totals
    const { totalCost, totalFlags, totalHandled } = useMemo(() => {
        let cost = 0
        let flags = 0
        let handled = 0
        unitInspection.area_inspections.forEach((areaIns) => {
            cost += areaIns.cost
            flags += areaIns.flagged_count
            handled += areaIns.handled_count
        })
        return { totalCost: cost, totalFlags: flags, totalHandled: handled }
    }, [unitInspection.area_inspections])

    const folderPath = unitInspection.folder?.path ?? ''
    const folderName = unitInspection.folder?.name ?? ''

    return (
        <div className={classes.cardContainer}>
            <div className={classes.timelineDot} />
            <Paper
                className={`${classes.card} ${
                    selected ? classes.cardSelected : ''
                }`}
                elevation={0}
                onClick={onClick}
            >
                {/* Top Row - Inspection info & Status/Cost */}
                <div className={classes.topRow}>
                    <div className={classes.titleSection}>
                        <span className={classes.inspectionName}>
                            {unitInspection.inspection?.name || 'Inspection'}
                        </span>
                        <span className={classes.folderPath}>
                            {folderPath}
                            {folderName}
                        </span>
                        <span className={classes.unitName}>
                            {unitInspection.unit.name}
                            {unitConfig ? ` • ${unitConfig.name}` : ''}
                        </span>
                    </div>

                    <div className={classes.rightSection}>
                        <UnitInspectionStatusBadge
                            unitInspection={unitInspection}
                        />
                        <NumberFormat
                            value={totalCost}
                            prefix="$"
                            thousandSeparator
                            displayType="text"
                            className={classes.cost}
                        />
                    </div>
                </div>

                {/* Bottom Row - Assignment & Badges */}
                <div className={classes.bottomRow}>
                    <div className={classes.assignmentInfo}>
                        <span className={classes.assignedTo}>
                            {unitInspection.assigned_to?.name ?? 'My Team'}
                        </span>
                        <span className={classes.dateRange}>
                            {toMMDDYYYY(new Date(unitInspection.start_date))} -{' '}
                            {toMMDDYYYY(new Date(unitInspection.end_date))}
                        </span>
                    </div>

                    <div className={classes.badges}>
                        <Badge badgeContent={totalFlags} color="secondary">
                            <FlagIcon
                                color={totalFlags > 0 ? undefined : 'disabled'}
                                fontSize="small"
                            />
                        </Badge>

                        <Badge badgeContent={totalHandled} color="primary">
                            <AssignmentTurnedIn
                                color={
                                    totalHandled > 0 ? undefined : 'disabled'
                                }
                                fontSize="small"
                            />
                        </Badge>
                    </div>
                </div>
            </Paper>
        </div>
    )
})
